import React, { useState } from 'react'
import PropTypes from 'prop-types'
import take from 'lodash/take'
import { useSelector, useDispatch } from 'react-redux'
import { formatISO } from 'lib/dates'
import toDate from 'date-fns-tz/toDate'

import { convertAgesToArray, areSameAges } from 'lib/paxes'
import { formatMappings } from 'src/comps/DateIntl/constants'
import { getIsFetching } from 'src/effects/ModifyDates/selectors'
import { modifyDatesRequestAction, saveDatesAndPaxesAction } from 'src/effects/ModifyDates/actions'
import { typeRef } from 'client/types'

import { useMarket } from 'src/hooks/useMarket'
import AgeSelector from 'src/comps/AgeSelector'
import { Button, Collapsible } from '@s4l/ds'
import DatePicker from 'client/components/DatePicker'
import IncrementSelector from 'client/components/IncrementSelector'
import InputReadOnly from 'client/components/InputReadOnly'
import DatesChangeConnected from 'src/comps/DatesChangeConnected'
import ModifyDatesDetails from 'src/comps/ModifyDates/ModifyDatesDetails'
import cn from 'classnames'
import { useS4LContext } from '@s4l/react-context'
import { useDevice } from 'src/hooks/useDevice'
import { BreakpointSizes } from 'src/types/device'
import Spinner from 'client/icons/Spinner'

import {
  AgeRange,
  CheckAvailabilityBase,
  ChildrenAge,
  Content,
  DateSelectorBase,
  ItemSelector,
  LeftRow,
  PaxWrapper,
  Row,
  SubTitle,
  Title,
  ModifyDatesAndGuests,
} from './styles'

const CheckAvailability = ({ existingBooking, modifyDatesSectionRef, propertyId, newDates }) => {
  const dispatch = useDispatch()

  const { language } = useMarket()

  const {
    adults: initialAdults,
    boardCode,
    hash: bookingHash,
    children: initialChildren,
    childrenAges: initialChildrenAges,
    checkIn: initialCheckIn,
    checkOut: initialCheckOut,
    language: languageBooking,
    market,
    rooms = 1,
  } = existingBooking

  const { i18n } = useS4LContext()
  const { activeBreakpoint } = useDevice()
  const isDesktop = activeBreakpoint === BreakpointSizes.L

  const isFetching = useSelector(getIsFetching)
  const childrenAgesFromBooking = convertAgesToArray(initialChildrenAges)

  const checkIn = toDate(initialCheckIn)
  const checkOut = toDate(initialCheckOut)

  const [adults, setAdults] = useState(newDates?.adults ?? parseInt(initialAdults))
  const [checkin, setCheckin] = useState(newDates?.checkIn ?? checkIn)
  const [checkout, setCheckout] = useState(newDates?.checkOut ?? checkOut)
  const [children, setChildren] = useState(
    newDates?.children ?? (initialChildren ? parseInt(initialChildren) : 0)
  )

  const [childrenAges, setChildrenAges] = useState(
    newDates?.childrenAges ?? childrenAgesFromBooking
  )
  const [focusedInput, setFocusedInput] = useState()
  const [isDatePickerOpen, setDatePickerOpen] = useState(false)

  const isMultiroom = rooms > 1

  function updateDates(payload) {
    if (payload.checkIn && payload.checkOut) {
      setCheckin(payload.checkIn)
      setCheckout(payload.checkOut)
    }
  }

  function handleOpen(input) {
    setFocusedInput(input)
    setDatePickerOpen(true)
  }

  function updatePaxes(eventData) {
    const { adults: changedAdults, children: changedChildren } = eventData

    if (changedAdults) {
      setAdults(changedAdults)
      return
    }

    setChildren(changedChildren)

    if (changedChildren < childrenAges.length) {
      const updatedArray = take(childrenAges, childrenAges.length - 1)
      setChildrenAges([...updatedArray])
      return
    }

    setChildrenAges([...childrenAges, { index: childrenAges.length, value: null }])
  }

  const updateAge = index => age => {
    const removed = childrenAges.filter(item => item.index !== index)
    const newElement = { index, value: age }
    const ordered = [...removed, newElement].sort((a, b) => a.index - b.index)
    setChildrenAges(ordered)
  }

  function isEnabledButton() {
    const validateAges = childrenAges.some(item => item.value === null)

    const changeDatesOrPaxes =
      checkin.getTime() !== checkIn.getTime() ||
      checkout.getTime() !== checkOut.getTime() ||
      initialAdults !== adults ||
      initialChildren !== children ||
      !areSameAges(childrenAgesFromBooking, childrenAges)

    return validateAges || !changeDatesOrPaxes
  }

  const childrenTitle = (
    <p>
      {i18n.formatMessage({ id: 'global.children' })}
      <AgeRange>{i18n.formatMessage({ id: 'global.age' })} 0-17</AgeRange>
    </p>
  )

  function handleChangeDatesAndPaxes() {
    dispatch(
      modifyDatesRequestAction({
        adults,
        boardCode,
        bookingHash,
        checkin: formatISO(checkin),
        checkout: formatISO(checkout),
        children,
        childrenAges,
        language: languageBooking,
        market,
        propertyId,
        rooms,
      })
    )

    dispatch(
      saveDatesAndPaxesAction({
        adults,
        checkin,
        checkout,
        children,
        childrenAges,
      })
    )
  }

  return (
    <CheckAvailabilityBase data-testid="modify-dates-section">
      <Collapsible
        defaultOpen
        title={i18n.formatMessage({ id: 'change-dates.original-dates-title' })}
        className={cn('mb-24', 'tablet:order-2')}
      >
        <ModifyDatesDetails booking={existingBooking} total />
      </Collapsible>

      <ModifyDatesAndGuests>
        <Title>
          {i18n.formatMessage(
            isMultiroom
              ? { id: 'global.modify-dates-multiroom' }
              : { id: 'global.modify-dates-paxes' }
          )}
        </Title>

        <SubTitle>
          {i18n.formatMessage(
            isMultiroom
              ? { id: 'global.select-dates-multiroom' }
              : { id: 'global.select-dates-paxes' }
          )}
        </SubTitle>

        <Row>
          <DateSelectorBase>
            <Content type="default" isFocus={isDatePickerOpen}>
              <InputReadOnly
                inputName="checkIn"
                labelMessage={{
                  id: 'search.checkin',
                }}
                onClick={() => handleOpen('checkin')}
                reference={modifyDatesSectionRef}
                type="default"
                value={i18n.formatDate(checkin, formatMappings.DATE_INTL_FORMAT_TWO_DIGITS)}
                dataTestId="check-in-test"
              />

              <InputReadOnly
                inputName="checkOut"
                labelMessage={{
                  id: 'search.checkout',
                }}
                onClick={() => handleOpen('checkout')}
                type="default"
                value={i18n.formatDate(checkout, formatMappings.DATE_INTL_FORMAT_TWO_DIGITS)}
                dataTestId="check-out-test"
              />

              {isDatePickerOpen && (
                <DatePicker
                  checkIn={checkin}
                  checkOut={checkout}
                  initialFocusedInput={focusedInput}
                  language={language}
                  onChangeDates={updateDates}
                  onClose={() => setDatePickerOpen(false)}
                  type="availability"
                  market={market}
                  unFreezeScroll
                />
              )}
            </Content>
          </DateSelectorBase>
        </Row>

        {!isMultiroom && (
          <>
            <Row>
              <PaxWrapper>
                <ItemSelector>
                  <IncrementSelector
                    maxLimit={9}
                    minLimit={1}
                    name="adults"
                    title={i18n.formatMessage({ id: 'global.adults' })}
                    value={adults}
                    update={updatePaxes}
                    position={0}
                  />
                </ItemSelector>

                <ItemSelector>
                  <IncrementSelector
                    maxLimit={9}
                    minLimit={0}
                    name="children"
                    title={childrenTitle}
                    value={children}
                    update={updatePaxes}
                    position={0}
                  />
                </ItemSelector>
              </PaxWrapper>
            </Row>

            <LeftRow>
              {childrenAges.map(item => (
                <ChildrenAge
                  data-testid={`age-select-${item.index}`}
                  key={`age-select-${item.index}`}
                >
                  <AgeSelector
                    initialAge={item.value}
                    item={item.value}
                    key={`${item.index}`}
                    titleTranslation={{
                      id: 'pax.children-ages-title',
                    }}
                    update={updateAge(item.index)}
                  />
                </ChildrenAge>
              ))}
            </LeftRow>
          </>
        )}
      </ModifyDatesAndGuests>
      <DatesChangeConnected
        trackingData={{
          context: 'booking_thanks',
          details: 'Check availability',
        }}
      >
        <Button
          data-testId="change-date-btn"
          disabled={isEnabledButton() || isFetching}
          fullWidth={!isDesktop}
          onClick={handleChangeDatesAndPaxes}
          variant="filled-rounded"
        >
          {isFetching ? (
            <Spinner size={18} />
          ) : (
            i18n.formatMessage({ id: 'global.availability-check' })
          )}
        </Button>
      </DatesChangeConnected>
    </CheckAvailabilityBase>
  )
}

CheckAvailability.propTypes = {
  existingBooking: PropTypes.shape({
    adults: PropTypes.number.isRequired,
    boardCode: PropTypes.string.isRequired,
    hash: PropTypes.string.isRequired,
    children: PropTypes.number.isRequired,
    childrenAges: PropTypes.string,
    checkIn: PropTypes.instanceOf(Date).isRequired,
    checkOut: PropTypes.instanceOf(Date).isRequired,
    language: PropTypes.string.isRequired,
    market: PropTypes.string.isRequired,
    rooms: PropTypes.number,
  }).isRequired,
  modifyDatesSectionRef: typeRef,
  propertyId: PropTypes.string.isRequired,
  newDates: PropTypes.shape({
    checkIn: PropTypes.instanceOf(Date),
    checkOut: PropTypes.instanceOf(Date),
    childrenAges: PropTypes.array,
    adults: PropTypes.number,
    children: PropTypes.number,
  }),
}

export default CheckAvailability
